import moment from 'moment-timezone';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { CommonIcons } from 'common-src/assets/Icons';
import { Button, RequestHandlerScreen } from 'common-src/components/base';
import { getRoleConfig } from 'common-src/features/auth';
import { getCurrentUserId } from 'common-src/features/auth/selectors';
import {
  deletePatientDocument,
  DocumentStatus,
  getPatientDocuments,
  getPatientDocumentsRequest,
  openDocument,
} from 'common-src/models/PatientDocument';
import { Colors, ColorsNew } from 'common-src/styles';

import { ModalType } from 'src/components/base/ModalGroup';
import {
  ActionsRow,
  BaseTable,
  CreatorItem,
  TablePagination,
  TextItem,
} from 'src/components/table';
import { ResultsCount } from 'src/components/table/TablePagination/constants';
import { TabsViewNew } from 'src/components/tabs';
import { openModal } from 'src/features/modals';
import useAlert from 'src/hooks/useAlert';
import useCustomSelector from 'src/hooks/useCustomSelector';
import useInterval from 'src/hooks/useInterval';
import useRequestLoading from 'src/hooks/useRequestLoading';

import { DefaultTabInfo, DocumentTabs, TableHeaders } from './constants';
import styles from './Documents.module.scss';
import { getTabInfo } from './helpers';

const Documents = ({ patientId }) => {
  const dispatch = useDispatch();

  const tabs = Object.keys(DocumentTabs);
  const [selectedTab, setSelectedTab] = useState(tabs[0]);
  const [filteredDocuments, setFilteredDocuments] = useState([]);
  const [tabInfo, setTabInfo] = useState(DefaultTabInfo);
  const [paginationData, setPaginationData] = useState({ from: 0, to: undefined });

  const currentUserId = useCustomSelector((state) => getCurrentUserId(state));
  const patientDocuments = useCustomSelector((state) => getPatientDocuments(state, patientId));
  const actions = useCustomSelector(
    (state) => getRoleConfig(state)?.patientChart?.sections?.documents,
  );

  const { showAlert, AlertType } = useAlert();
  const { isRequesting } = useRequestLoading([getPatientDocumentsRequest({ patientId })]);

  useInterval(() => {
    dispatch(getPatientDocumentsRequest({ patientId, sort_order: 'desc', limit: 5 }, {}));
  });

  useEffect(() => {
    const { tabInfo, uploadedDocuments, deletedDocuments, allDocuments } =
      getTabInfo(patientDocuments);
    setTabInfo(tabInfo);

    switch (selectedTab) {
      case tabs[0]:
        setFilteredDocuments(uploadedDocuments);
        break;
      case tabs[1]:
        setFilteredDocuments(deletedDocuments);
        break;
      default:
        setFilteredDocuments(allDocuments);
        break;
    }
  }, [selectedTab, patientDocuments]);

  const openDocumentHandler = (id, fileName) => {
    openDocument(id, fileName, (error) => {
      showAlert(AlertType.Error, '', error.message);
    });
  };

  const getData = () =>
    filteredDocuments.map((document) => {
      const isLink = document.fileType === 'application/link';
      return {
        fileName: (
          <TextItem
            isActive
            text={document.fileName}
            isUnderlined
            onClick={() => {
              if (isLink) {
                window.open(document.fileName, '_blank', 'noopener');
              } else {
                openDocumentHandler(document.id, document.fileName);
              }
            }}
          />
        ),
        fileType: <TextItem isActive text={document.fileType} />,
        fileCategory: <TextItem isActive text={document.fileCategory} />,
        uploadedAt: (
          <TextItem
            isActive
            text={moment(document.createdAt).format('MM/DD/YYYY')}
            value={document.createdAt}
          />
        ),
        uploadedBy: (
          <CreatorItem
            isActive
            creator={document?.createdBy}
            creatorName={document?.createdBy?.getName(false, true)}
            value={document?.createdBy?.getName(false, true)}
          />
        ),
        actions: (
          <ActionsRow
            options={[
              isLink
                ? {
                    id: 'open-link-button',
                    text: 'Open',
                    backgroundColor: '#555671B2',
                    color: 'white',
                    onClick: () => window.open(document.fileName, '_blank', 'noopener'),
                  }
                : {
                    id: 'download-button',
                    text: 'Download',
                    backgroundColor: '#555671B2',
                    color: 'white',
                    onClick: () => openDocumentHandler(document.id, document.fileName),
                  },
              document.status === DocumentStatus.Uploaded && {
                id: 'delete-button',
                text: 'Delete',
                backgroundColor: '#F01F51B2',
                color: 'white',
                showWarning: true,
                disabled: document.createdBy?.id !== currentUserId && !actions?.deleteDocument,
                onClick: () =>
                  dispatch(
                    deletePatientDocument(document.id, {
                      successBlock: () => {
                        showAlert(AlertType.Success, '', 'Document deleted!');
                      },
                      errorBlock: (err) => showAlert(AlertType.Error, '', err),
                    }),
                  ),
              },
            ]}
          />
        ),
      };
    });

  if (isRequesting) {
    return <RequestHandlerScreen isRequesting requestingText="Pulling documents..." />;
  }

  return (
    <>
      <TabsViewNew
        classNames={['medical-tabs']}
        options={tabInfo}
        onTabSelected={setSelectedTab}
        showZero
        rightContent={
          <Button
            id="upload-document-button"
            text="Upload Document"
            onClick={() => dispatch(openModal(ModalType.UPLOAD_DOCUMENT, { patientId }))}
            backgroundColor={Colors.white}
            textColor={ColorsNew.darkGreen}
            iconSrc={CommonIcons.uploadIcon}
            classNames={[styles.button]}
          />
        }
      />
      <BaseTable
        headers={TableHeaders}
        data={getData()}
        name="sdoh"
        classNames={[
          'medical-table-new',
          filteredDocuments?.length <= ResultsCount[25] ? 'no-pagination' : '',
        ]}
        emptyText="No documents"
        emptyClassNames={['medical-table-empty']}
        initialSortColumn={{
          column: 'uploadedAt',
          label: 'Uploaded At',
          width: '15%',
          sortBy: 'value',
        }}
        paginationData={paginationData}
      />
      <TablePagination onChangePageHandler={setPaginationData} results={filteredDocuments} />
    </>
  );
};

Documents.propTypes = {
  patientId: PropTypes.number,
};

export default Documents;
